import { type QueryStatusListItem, type QueryStatusListQuery } from '@utazzo/shared';
import { UzBadge, UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { QueryStatusFormDrawer } from '../components/QueryStatusFormDrawer';
import { listQueryStatuses } from '../query-statuses.api';
import {
  useBulkDeleteQueryStatuses,
  useBulkSetQueryStatusesStatus,
  useDeleteQueryStatus,
  useQueryStatusesList,
} from '../use-query-statuses';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

/**
 * Renders the outcome badge for a status row. A status may be won, lost, or
 * neither - the two flags are mutually exclusive in practice.
 */
const OutcomeBadge = ({ isWon, isLost }: { isWon: boolean; isLost: boolean }): React.JSX.Element =>
  isWon ? (
    <UzBadge tone="success">Won</UzBadge>
  ) : isLost ? (
    <UzBadge tone="destructive">Lost</UzBadge>
  ) : (
    <span className="text-zinc-400">-</span>
  );

export const QueryStatusesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteQueryStatus = useDeleteQueryStatus();
  const bulkDeleteMut = useBulkDeleteQueryStatuses();
  const bulkSetStatusMut = useBulkSetQueryStatusesStatus();

  const columns: ListScreenColumns<QueryStatusListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<QueryStatusListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'parent',
        header: 'Parent',
        cell: (row) => <span className="text-zinc-600">{row.parent?.name ?? '-'}</span>,
      },
      {
        id: 'bucket',
        header: 'Bucket',
        cell: (row) => <span className="text-zinc-600">{row.bucket ?? '-'}</span>,
      },
      {
        id: 'color',
        header: 'Color',
        cell: (row) =>
          row.color ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block h-3.5 w-3.5 rounded-full border border-zinc-200"
                style={{ backgroundColor: row.color }}
                aria-hidden
              />
              <span className="font-mono text-xs text-zinc-600">{row.color}</span>
            </span>
          ) : (
            <span className="text-xs text-zinc-400">inferred</span>
          ),
      },
      {
        id: 'outcome',
        header: 'Outcome',
        cell: (row) => <OutcomeBadge isWon={row.isWon} isLost={row.isLost} />,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<QueryStatusListItem, QueryStatusListQuery>
      title="Query Statuses"
      description="Lead-pipeline stages; a status may have a parent (sub-status)."
      useList={useQueryStatusesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add query status' }}
      renderDrawer={({ open, mode, row, close }) => (
        <QueryStatusFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          queryStatus={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteQueryStatus.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this query status?',
            description: `${row.name} will be soft-deleted and hidden from lists. Leads already assigned to it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} query status${rows.length === 1 ? '' : 'es'}?`,
            description: 'These query statuses will be soft-deleted and hidden from lists.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} query status${rows.length === 1 ? '' : 'es'}?`,
            description: isActive
              ? 'They will become selectable again in lead capture and reports.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listQueryStatuses(q),
        filename: 'query-statuses',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Parent', value: (r) => r.parent?.name ?? '' },
          { header: 'Bucket', value: (r) => r.bucket ?? '' },
          { header: 'Color', value: (r) => r.color ?? '' },
          { header: 'Outcome', value: (r) => (r.isWon ? 'Won' : r.isLost ? 'Lost' : '') },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name, code, or description (min 3 characters)',
        emptyTitle: 'No query statuses match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load query statuses',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
